import { projectIdOfUid } from '@/api/puppets'
import { thumbUrl } from '@/api/skel'
import { toWireItem, type SceneryBand, type SceneryCandidate, type SceneryItem } from '@/api/stages'

/**
 * The scenery layout as plain data, and every edit the editor makes to it — pure functions, so a
 * drag, a resize or a reorder can be tested without a browser. Coordinates are percent of the
 * stage: `x`/`y` is where the thing touches the ground (its feet), `height_pct` its height.
 */

export const MIN_HEIGHT_PCT = 2
export const MAX_HEIGHT_PCT = 90

/** Where a new thing's feet go, by band. */
export const BAND_Y: Record<SceneryBand, number> = { sky: 24, mid: 60, ground: 90 }

/** Usual heights when the catalog has none. */
export const BAND_HEIGHT: Record<SceneryBand, number> = { sky: 12, mid: 18, ground: 28 }

/** Drift speeds a parent picks from (percent of the stage width per second). */
export const DRIFT_SPEEDS = [
  { id: 'slow', label: 'Slowly', dx: 0.8 },
  { id: 'normal', label: 'At a walk', dx: 1.6 },
  { id: 'fast', label: 'Quickly', dx: 3.2 },
] as const

export const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v))

export const clampX = (x: number): number => clamp(x, 0, 100)
export const clampY = (y: number): number => clamp(y, 2, 100)
export const clampHeight = (h: number): number => clamp(h, MIN_HEIGHT_PCT, MAX_HEIGHT_PCT)

/** Back to front: the order things are drawn in. */
export function byZ(items: readonly SceneryItem[]): SceneryItem[] {
  return [...items].sort((a, b) => a.z - b.z || a.id.localeCompare(b.id))
}

/** z = 0..n-1 in drawing order (keeps the order, removes gaps and ties). */
export function normalizeZ(items: readonly SceneryItem[]): SceneryItem[] {
  const order = new Map(byZ(items).map((it, i) => [it.id, i]))
  return items.map((it) => (it.z === order.get(it.id) ? it : { ...it, z: order.get(it.id)! }))
}

/** A fresh id: `s1`, `s2`, … not already used. */
export function newId(items: readonly SceneryItem[]): string {
  const taken = new Set(items.map((i) => i.id))
  for (let n = items.length + 1; n < 10_000; n++) if (!taken.has(`s${n}`)) return `s${n}`
  return `s${Date.now().toString(36)}`
}

/** Spots a new thing may take, best first (the sides, then the middle, then the far edges). */
const SPOTS = [18, 82, 34, 66, 50, 8, 92, 26, 74, 42, 58]
/** Far enough from its neighbours not to overlap them much. */
const ROOMY_PCT = 20

/** The first roomy spot in that band, else the one farthest from everything there. */
export function freeX(items: readonly SceneryItem[], band: SceneryBand): number {
  const same = items.filter((i) => i.band === band).map((i) => i.x)
  if (same.length === 0) return SPOTS[0]!
  const gapAt = (s: number) => Math.min(...same.map((x) => Math.abs(x - s)))
  const roomy = SPOTS.find((s) => gapAt(s) >= ROOMY_PCT)
  if (roomy !== undefined) return roomy
  return SPOTS.reduce((best, s) => (gapAt(s) > gapAt(best) + 0.01 ? s : best), SPOTS[0]!)
}

/** A candidate, placed where it belongs (the stage's own row for its band when the server sized it for this stage), in front of everything else. */
export function itemFromCandidate(c: SceneryCandidate, items: readonly SceneryItem[]): SceneryItem {
  const band = c.band
  const clip = c.ambient ?? null
  return {
    id: newId(items),
    puppet_uid: c.puppet_uid,
    slug: c.slug,
    name: c.name,
    band,
    x: freeX(items, band),
    y: clampY(c.default_y ?? BAND_Y[band]),
    height_pct: clampHeight(c.height_pct > 0 ? c.height_pct : BAND_HEIGHT[band]),
    flip: false,
    clip,
    drift: clip === 'drift' ? { dx_per_s: DRIFT_SPEEDS[0].dx, wrap: true } : null,
    z: items.reduce((m, i) => Math.max(m, i.z), -1) + 1,
    thumb: c.thumb,
  }
}

export function updateItem(items: readonly SceneryItem[], id: string, patch: Partial<Omit<SceneryItem, 'id'>>): SceneryItem[] {
  return items.map((it) => (it.id === id ? { ...it, ...patch } : it))
}

export function moveItem(items: readonly SceneryItem[], id: string, x: number, y: number): SceneryItem[] {
  return updateItem(items, id, { x: clampX(x), y: clampY(y) })
}

export function resizeItem(items: readonly SceneryItem[], id: string, heightPct: number): SceneryItem[] {
  return updateItem(items, id, { height_pct: clampHeight(heightPct) })
}

/** A new band also moves the thing to that band's height on the picture. */
export function setBand(items: readonly SceneryItem[], id: string, band: SceneryBand): SceneryItem[] {
  return items.map((it) => (it.id === id ? { ...it, band, y: it.band === band ? it.y : BAND_Y[band] } : it))
}

export function removeItem(items: readonly SceneryItem[], id: string): SceneryItem[] {
  return normalizeZ(items.filter((it) => it.id !== id))
}

/** One step toward the front (`+1`) or the back (`-1`). */
export function shiftZ(items: readonly SceneryItem[], id: string, dir: 1 | -1): SceneryItem[] {
  const order = byZ(normalizeZ(items))
  const i = order.findIndex((it) => it.id === id)
  const j = i + dir
  if (i < 0 || j < 0 || j >= order.length) return [...items]
  const swapped = [...order]
  ;[swapped[i], swapped[j]] = [swapped[j]!, swapped[i]!]
  const z = new Map(swapped.map((it, k) => [it.id, k]))
  return items.map((it) => ({ ...it, z: z.get(it.id)! }))
}

/** Same layout on the wire (ignores the client-only picture and float noise). */
export function sameItems(a: readonly SceneryItem[], b: readonly SceneryItem[]): boolean {
  if (a.length !== b.length) return false
  const key = (xs: readonly SceneryItem[]) => JSON.stringify([...xs].sort((p, q) => p.id.localeCompare(q.id)).map(toWireItem))
  return key(a) === key(b)
}

/** The picture of a thing: its own, the candidate's, else the puppet's rendered thumbnail. */
export function itemThumb(item: Pick<SceneryItem, 'thumb' | 'puppet_uid'>, candidates?: ReadonlyMap<string, SceneryCandidate>): string | null {
  if (item.thumb) return item.thumb
  const c = candidates?.get(item.puppet_uid)
  if (c?.thumb) return c.thumb
  const pid = projectIdOfUid(item.puppet_uid)
  return pid ? thumbUrl(pid) : null
}

/** A drift's direction and speed, read back for the controls. */
export function driftParts(drift: SceneryItem['drift']): { on: boolean; dir: 1 | -1; speed: (typeof DRIFT_SPEEDS)[number]['id'] } {
  if (!drift) return { on: false, dir: 1, speed: 'slow' }
  const abs = Math.abs(drift.dx_per_s)
  const speed = DRIFT_SPEEDS.reduce((best, s) => (Math.abs(s.dx - abs) < Math.abs(best.dx - abs) ? s : best), DRIFT_SPEEDS[0] as (typeof DRIFT_SPEEDS)[number]).id
  return { on: true, dir: drift.dx_per_s < 0 ? -1 : 1, speed }
}

export function makeDrift(dir: 1 | -1, speed: (typeof DRIFT_SPEEDS)[number]['id']): NonNullable<SceneryItem['drift']> {
  const s = DRIFT_SPEEDS.find((d) => d.id === speed) ?? DRIFT_SPEEDS[0]
  return { dx_per_s: dir * s.dx, wrap: true }
}
